/**
 * GDPR Utility Module for InMobi Choice CMP (TCF v2.2)
 */

// List of European countries (EU, EEA, UK, CH)
const EUROPEAN_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'IS', 'LI',
  'NO', 'CH', 'IN'
];


/**
 * Checks if a country code belongs to the European region (EU / EEA / UK / CH)
 * @param {string} countryCode 
 * @returns {boolean}
 */
export const isEuropeanCountry = (countryCode) => {
  if (!countryCode || typeof countryCode !== 'string') return false;
  return EUROPEAN_COUNTRIES.includes(countryCode.trim().toUpperCase());
};

/**
 * Safely fetches TCData from InMobi Choice __tcfapi (TCF v2.2)
 * @returns {Promise<{ gdprApplies: boolean, tcString: string, cmpLoaded: boolean }>}
 */
export const getGdprTCData = () => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.__tcfapi !== 'function') {
      return resolve({ gdprApplies: false, tcString: '', cmpLoaded: false });
    }

    let timeoutId = setTimeout(() => {
      resolve({ gdprApplies: false, tcString: '', cmpLoaded: false });
    }, 1500);

    try {
      window.__tcfapi('getTCData', 2, (tcData, success) => {
        clearTimeout(timeoutId);
        if (success && tcData) {
          resolve({
            gdprApplies: !!tcData.gdprApplies,
            tcString: tcData.tcString || '',
            cmpLoaded: true,
            eventStatus: tcData.eventStatus || ''
          });
        } else {
          resolve({ gdprApplies: false, tcString: '', cmpLoaded: false });
        }
      });
    } catch (err) {
      clearTimeout(timeoutId);
      resolve({ gdprApplies: false, tcString: '', cmpLoaded: false });
    }
  });
};

/**
 * Suppresses or shows the InMobi Choice CMP modal based on subscription and region
 * @param {Object} params
 * @param {boolean} params.isSubscribed - Whether user has an active subscription
 * @param {string} [params.countryCode] - Geo country code
 */
export const manageGdprConsentModal = async ({ isSubscribed, countryCode }) => {
  if (typeof document === 'undefined') return;

  const styleId = 'inmobi-choice-gdpr-suppress-style';
  let styleEl = document.getElementById(styleId);

  // If user is subscribed, GDPR modal is NOT applicable -> Suppress CMP modal UI
  if (isSubscribed) {
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.innerHTML = `
        #choice-consent-banner,
        .qc-cmp2-container,
        .qc-cmp-cleanslate,
        iframe[name="__tcfapiLocator"],
        div[id*="sp_message_container"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(styleEl);
    }
    return;
  }

  // If user is unsubscribed, remove suppression CSS if present
  if (styleEl) {
    styleEl.remove();
  }

  // Check if region is European
  const tcData = await getGdprTCData();
  const isEU = tcData.gdprApplies || isEuropeanCountry(countryCode);

  // For unsubscribed European users, trigger CMP UI if available and needed
  if (isEU && typeof window.__tcfapi === 'function') {
    try {
      window.__tcfapi('displayConsentUi', 2, (success) => {
        // UI displayed if user hasn't consented
      });
    } catch (e) {
      // Ignore if CMP manages UI auto-display
    }
  }
};

/**
 * Replaces GDPR & consent macros in ad URLs / SSAI live stream URLs
 * @param {string} adUrl - Ad URL or live stream playlist URL
 * @param {Object} options
 * @param {boolean} [options.isSubscribed=false]
 * @param {string} [options.countryCode]
 * @param {string} [options.tcString]
 * @param {boolean} [options.gdprApplies]
 * @returns {string} Processed URL
 */
export const replaceGdprMacros = (adUrl, { isSubscribed = false, countryCode = '', tcString = '', gdprApplies = false } = {}) => {
  if (!adUrl || typeof adUrl !== 'string') return adUrl || '';

  const isEU = !isSubscribed && (gdprApplies || isEuropeanCountry(countryCode));
  const gdprVal = isEU ? '1' : '0';
  const consentVal = isEU ? (tcString || '0') : '0';

  let updatedUrl = adUrl;

  // 1. Bracket macros replacement
  updatedUrl = updatedUrl
    .replace(/\[GDPR\]/gi, gdprVal)
    .replace(/\[CONSENT\]/gi, consentVal)
    .replace(/\[GDPR_CONSENT\]/gi, consentVal)
    .replace(/\[TC_STRING\]/gi, consentVal)
    .replace(/\[TCSTRING\]/gi, consentVal);

  // 2. Query param value replacement if explicit parameters exist
  // Handles: gdpr=... or consent=... or gdpr_consent=...
  try {
    const urlObj = new URL(updatedUrl, window?.location?.origin || 'https://localhost');
    let hasParamChanges = false;

    if (urlObj.searchParams.has('gdpr')) {
      urlObj.searchParams.set('gdpr', gdprVal);
      hasParamChanges = true;
    }
    if (urlObj.searchParams.has('consent')) {
      urlObj.searchParams.set('consent', consentVal);
      hasParamChanges = true;
    }
    if (urlObj.searchParams.has('gdpr_consent')) {
      urlObj.searchParams.set('gdpr_consent', consentVal);
      hasParamChanges = true;
    }

    if (hasParamChanges) {
      updatedUrl = urlObj.toString();
    }
  } catch (e) {
    // String replacement fallback if URL parsing fails
    updatedUrl = updatedUrl
      .replace(/([?&]gdpr=)[^&]*/gi, `$1${gdprVal}`)
      .replace(/([?&]consent=)[^&]*/gi, `$1${consentVal}`)
      .replace(/([?&]gdpr_consent=)[^&]*/gi, `$1${consentVal}`);
  }

  return updatedUrl;
};
